import { nextTick, effectScope } from 'vue'

import LoginPage from '@/apps/MainApp/views/LoginPage.vue'
import InboxPage from '@/apps/MainApp/components/inbox/InboxPage.vue'
import AdminPage from '@/apps/MainApp/views/AdminPage.vue'
import AppDashboard from '@/apps/MainApp/views/app-dashboard/AppDashboard.vue'
import CronofyCallback from '@/apps/MainApp/components/CronofyCallback.vue'
import DisclaimerRedirect from '@/apps/MainApp/components/DisclaimerRedirect.vue'

import valuationRoutes from '@/apps/MainApp/routes'
import ersRoutes from '@/apps/ERS/routes'
import oepcRoutes from '@/apps/OEPC/routes'
import catalogRoutes from '@/apps/Catalog/router/routes'
import store from '@/store'
import { Actions } from '@/store/Log/index.js'
import { Actions as AuthActions } from '@/store/Auth/index.js'
import axios from '@/plugins/axios.js'
import utils from '@/plugins/utils.js'
import MainDashboard from '@/apps/MainApp/views/Dashboard/Views/DashboardPage.vue'
import ImpersonateLogin from '@/apps/MainApp/views/UserImpersonationLogin.vue'
import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { useDashboardRedirect } from '@/composables/useDashboardRedirect'
import { useDashboardRequestList } from '@/composables/useDashboardRequestList'

const routes = [
  {
    path: '/',
    name: 'base',
    redirect: (to) => {
      const config = useConfig()
      const defaultHomepage = config.DEFAULT_HOMEPAGE || 'dashboard'
      return { name: defaultHomepage, params: to.params, query: to.query }
    },
    beforeEnter: async (to, _from) => {
      if (to.redirectedFrom?.name === 'not_found') {
        // TODO: add 404 message string to i18n:
        store.dispatch(Actions.LOG_ERROR, {
          error: {
            error_code: 'route_not_found',
            debug: {
              message: `Route not found: ${to.fullPath}`,
            },
          },
          errorType: 'warning',
        })
      }
    },
    meta: {
      resetCurRequest: true,
    },
  },
  {
    path: '/apps',
    name: 'appDashboard',
    component: AppDashboard,
    meta: {
      resetCurRequest: true,
    },
  },
  {
    path: '/p/disclaimer',
    name: 'disclaimer',
    component: DisclaimerRedirect,
    meta: {
      public: true,
    },
  },
  {
    path: '/default',
    redirect: '/',
  },
  {
    path: '/be-EN',
    redirect: '/en-BE', // This is needed because the next one expects a route (doesn't catch /be-EN/)
  },
  {
    path: '/be-EN/:route',
    // redirect: '/en-BE/:route',
    redirect: (to) => {
      return `/en-BE/${to.params.route}`
    },
  },
  {
    path: '/be-NL',
    redirect: '/nl-BE',
  },
  {
    path: '/be-NL/:route',
    // redirect: '/nl-BE/:route',
    redirect: (to) => {
      return `/nl-BE/${to.params.route}`
    },
  },
  {
    path: '/be-FR',
    redirect: '/fr-BE',
  },
  {
    path: '/be-FR/:route',
    // redirect: '/fr-BE/:route',
    redirect: (to) => {
      return `/fr-BE/${to.params.route}`
    },
  },
  {
    name: 'login',
    component: LoginPage,
    path: '/login',
    alias: '/default/login',
  },
  {
    name: 'sso_login',
    path: '/sso_login',
    redirect: {
      name: 'login',
    },
  },
  {
    name: 'knowledge-base',
    path: '/knowledge-base',
    component: () => import('@/apps/MainApp/views/support/KnowledgeBasePage.vue'),
  },
  {
    name: 'knowledge-base-assistant',
    path: '/knowledge-base-assistant',
    component: () => import('@/apps/MainApp/views/support/KnowledgeBaseAssistant.vue'),
    beforeEnter: (to, _, next) => {
      const { is: userIs } = useCurUser()

      if (!userIs.value.catSubmitter) {
        return next({ name: 'dashboard', params: { lang: to.params.lang } })
      }

      return next()
    },
  },
  {
    name: 'inbox',
    component: InboxPage,
    path: '/inbox',
    beforeEnter: (to, _, next) => {
      const config = useConfig()

      if (!config.TALKJS_APP_ID) {
        return next({ name: 'dashboard', params: { lang: to.params.lang } })
      }

      return next()
    },
  },
  {
    name: 'admin',
    component: AdminPage,
    path: '/admin',
    beforeEnter: (to, _, next) => {
      const scope = effectScope()
      scope.run(() => {
        // NOTE: using effectScope to be able to use composables before App is mounted
        const config = useConfig()
        const { is: userIs } = useCurUser()
        if (config.CHECK_LOGIN && !userIs.value.admin)
          return next({ name: 'dashboard', params: { lang: to.params.lang } })
        return next()
      })
      scope.stop()
    },
    meta: {
      resetCurRequest: true,
    },
  },
  {
    name: 'impersonateLogin',
    path: '/login-basic-auth',
    component: ImpersonateLogin,
    beforeEnter: (to, _, next) => {
      const scope = effectScope()
      scope.run(() => {
        const config = useConfig()
        const { is: userIs } = useCurUser()
        if (!config.ENABLE_DEV_FEATURES && !userIs.value.admin)
          return next({ name: 'dashboard', params: { lang: to.params.lang } })
        return next()
      })
      scope.stop()
    },
  },
  {
    name: 'dashboard',
    component: MainDashboard,
    path: '/dashboard',
    meta: {
      resetCurRequest: true,
    },
    beforeEnter: async (to, _from) => {
      const scope = effectScope()
      const { getRedirect } = useDashboardRedirect()
      const requestList = scope.run(() => useDashboardRequestList())
      try {
        await requestList.suspense()
        return getRedirect(to, requestList?.data ?? [])
      } finally {
        scope.stop()
      }
    },
  },
  {
    path: '/dashboard/v1',
    redirect: (to) => ({
      // TODO: remove by end of year 2025
      path: '/dashboard',
      query: to.query,
    }),
  },
  {
    path: '/cronofy-callback',
    name: 'cronofy-callback',
    component: CronofyCallback,
  },
  {
    path: '/invalid',
    name: 'invalid',
    component: () => import('@/apps/MainApp/views/InvalidPage.vue'),
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/apps/MainApp/views/ForbiddenPage.vue'),
  },
  ...valuationRoutes,
  ...ersRoutes,
  ...oepcRoutes,
  ...catalogRoutes,
  {
    path: '/default/:route',
    redirect: (to) => {
      console.debug('redirecting to', to.params.route)
      return `/${to.params.route}`
    },
  },
  {
    path: '/referral/:type',
    name: 'referral',
    component: () => import('@/apps/MainApp/components/Referral/ReferralPage.vue'),
    meta: {
      public: true,
    },
    beforeEnter: async (to, from, next) => {
      const config = useConfig()
      nextTick(() => {
        axios.get(utils.urlJoin(config.AUTH_API_URL, 'logout'))
        store.dispatch(AuthActions.LOGOUT)
      })
      next()
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not_found',
    redirect: '/',
  },
]

routes.push({
  path: '/dev',
  beforeEnter: (_to, _from, next) => {
    const scope = effectScope()
    scope.run(() => {
      // Always enabled in development mode, restricted to devs in production:
      if (import.meta.env.MODE !== 'development') {
        // NOTE: cannot use inject here (not yet available before App component is mounted):
        const { hasRole } = useCurUser()
        if (!hasRole('developer')) {
          console.warn('This route is only available to developers')
          return next({ name: 'appDashboard' })
        }
      }
      return next()
    })
    scope.stop()
  },
  children: [
    {
      name: 'debug',
      path: 'debug',
      component: () => import('@/apps/MainApp/views/DebugPage.vue'),
    },
    {
      name: 'playground',
      path: 'playground',
      component: () => import('@/apps/MainApp/views/PlaygroundPage.vue'),
    },
    {
      name: 'print-report',
      path: 'print-report',
      component: () => import('@/apps/MainApp/views/PrintReport.vue'),
    },
    {
      name: 'tester',
      path: 'tester',
      component: () => import('@/apps/MainApp/views/TesterPage.vue'),
    },
    {
      name: 'configuration',
      component: () => import('@/apps/MainApp/views/ConfigurationPage.vue'),
      path: 'configuration',
      meta: {
        resetCurRequest: true,
      },
    },
  ],
})

export default routes
